import React, { useEffect } from "react";

import classNames from "classnames";

import Panel from "components/Panel";
import PanelSection from "components/PanelSection";
import ExplainNodeTree from "components/ExplainNodeTree";
import ExplainSidebar from "components/ExplainSidebar";
import ExpandableSQL from "components/ExpandableSQL";
import WithNodeSelection from "components/WithExplainNodeSelection";
import PillButtonBar from "components/PillButtonBar";

import moment from "moment-timezone";

import { formatTimestampShort } from "utils/format";

import ExplainComparison, {
  useComparisonPlanIds,
} from "components/ExplainComparison";
import ExplainViewSource from "./ExplainViewSource";
import WithExplainDisplayOptions from "components/WithExplainDisplayOptions";
import type { ExplainPlanType, QuerySampleType } from "utils/explain";

import styles from "./style.module.scss";
import WithExplainPlan, {
  ComparablePlanType,
  ComparablePlanSummaryType,
} from "components/WithExplainPlan";
import { AnnotatedPlan } from "types/explain";
import ExplainCompareSidebar from "components/ExplainCompareSidebar";
import QueryParameters from "components/QueryParameters";
import { useLocation } from "react-router-dom";
import Loading from "components/Loading";

import { useRoutes } from "utils/routes";
import {
  ExplainComparisonQueryHookResult,
  useExplainComparisonQuery,
} from "./gql/Query.generated";
import ExplainGridView from "components/ExplainGridView";
import ExplainSidebarV2 from "components/ExplainSidebarV2";
import { combineInsights, QueryTuningInsight } from "utils/explain";
import { useFeature } from "components/OrganizationFeatures";
import { useUserPreferences } from "utils/hooks";

type ComparedPlan = ExplainComparisonQueryHookResult["data"]["planA"];

type Props = {
  explain: ExplainPlanType;
  databaseId: string;
  blockSize: number;
  relatedWorkbookId?: string;
} & (
  | {
      hideComparison?: false;
      comparablePlans: ComparablePlanSummaryType[];
    }
  | {
      hideComparison: true;
      comparablePlans?: ComparablePlanSummaryType[];
    }
);

type ViewType = "grid" | "viz" | "text" | "json" | "comparison";

const ExplainPanel: React.FunctionComponent<Props> = ({
  explain,
  databaseId,
  blockSize,
  hideComparison,
  comparablePlans,
  relatedWorkbookId,
}) => {
  const { databaseQueryExplainCompare } = useRoutes();
  const currView = useCurrentView();
  const viewLink = useViewLink();
  const hasQueryAdvisor = useFeature("queryAdvisor");

  const pillButtonOptions = [
    { value: viewLink("grid"), label: "Grid" },
    { value: viewLink("viz"), label: "Node Tree" },
    { value: viewLink("text"), label: "Text" },
    { value: viewLink("json"), label: "JSON" },
  ];
  if (!hideComparison) {
    pillButtonOptions.push({
      value: viewLink("comparison"),
      label: "Compare Plans",
    });
  }

  const [planAId, planBId] = useComparisonPlanIds();

  const skipCompareQuery = currView !== "comparison" || !planAId || !planBId;
  const { error, loading, data } = useExplainComparisonQuery({
    variables: {
      databaseId,
      planAId,
      planBId,
    },
    skip: skipCompareQuery,
  });

  if (!skipCompareQuery && (loading || error)) {
    return <Loading error={!!error} />;
  }

  const currentPlan = toComparablePlan(data?.planA ?? explain, blockSize);
  const comparePlan = data?.planB
    ? toComparablePlan(data.planB, blockSize)
    : undefined;
  const plan = currentPlan.plan;
  // Filter out legacy annotations that are now covered by Query Advisor insights:
  //   - "inefficient nested loop" → "query advisor/inefficient nested loop"
  //   - "disk sort" and "hash batches" → "query advisor/work mem spill"
  // Only do this for users with the Query Advisor feature so that users without QA can still see these annotations.
  if (hasQueryAdvisor) {
    const legacyCoveredByQueryAdvisor = new Set([
      "inefficient nested loop",
      "disk sort",
      "hash batches",
    ]);
    plan.annotations = plan.annotations.filter(
      (ann) => !legacyCoveredByQueryAdvisor.has(ann.type),
    );
  }

  function linkToComparison(planA: string, planB?: string) {
    return databaseQueryExplainCompare(
      databaseId,
      explain.query.id,
      planA,
      planB,
    );
  }

  return (
    <div className="px-[20px] flex-grow h-min overflow-auto">
      <div className="h-full flex">
        <WithExplainPlan plan={currentPlan} comparePlan={comparePlan}>
          <WithNodeSelection>
            <WithExplainDisplayOptions>
              <div className="flex-grow relative overflow-y-scroll pl-1 -ml-1 pr-4">
                <div
                  className={classNames(
                    "py-4 mb-1 flex gap-5 sticky top-0 z-50 px-1 -mx-1",
                    styles.buttonBar,
                  )}
                >
                  <PillButtonBar
                    opts={pillButtonOptions}
                    selected={viewLink(currView)}
                  />
                </div>
                <QueryPanel querySample={explain.querySample} />
                {currView === "comparison" ? (
                  <ExplainComparison
                    comparablePlans={comparablePlans}
                    linkToComparison={linkToComparison}
                    blockSize={blockSize}
                  />
                ) : currView === "json" || currView === "text" ? (
                  <div className="mt-8">
                    <ExplainViewSource
                      format={currView}
                      annotatedPlan={plan}
                      textPlan={explain.outputText}
                    />
                  </div>
                ) : currView === "viz" ? (
                  <ExplainNodeTree plan={plan} databaseId={databaseId} />
                ) : currView === "grid" ? (
                  <div className="mb-4">
                    <ExplainGridView
                      databaseId={databaseId}
                      plan={plan}
                      blockSize={blockSize}
                    />
                  </div>
                ) : null}
              </div>
              {/* N.B.: pb is to clear the Get Help button in the lower right corner */}
              <div className="p-4 pb-[80px] border-l 2xl:w-[480px] w-[400px] -mr-4 shrink-0 overflow-y-scroll">
                {currView === "comparison" ? (
                  <ExplainCompareSidebar
                    blockSize={blockSize}
                    databaseId={databaseId}
                  />
                ) : currView === "grid" ? (
                  <ExplainSidebarV2
                    databaseId={databaseId}
                    blockSize={blockSize}
                    ioBlocks={explain.totalSharedBlksRead}
                    queryId={explain.query.id}
                    relatedWorkbookId={relatedWorkbookId}
                    plan={currentPlan}
                  />
                ) : (
                  <ExplainSidebar
                    explain={explain}
                    blockSize={blockSize}
                    plan={currentPlan}
                    relatedWorkbookId={relatedWorkbookId}
                    databaseId={databaseId}
                    summaryOnly={currView === "json" || currView === "text"}
                  />
                )}
              </div>
            </WithExplainDisplayOptions>
          </WithNodeSelection>
        </WithExplainPlan>
      </div>
    </div>
  );
};

function toComparablePlan(
  // TODO: fix type situation here
  explain: ComparedPlan & { advisorInsights?: QueryTuningInsight[] },
  blockSize: number,
): ComparablePlanType {
  const plan: AnnotatedPlan = explain.annotatedJson;

  const qaInsights = explain.advisorInsights ?? [];

  const annotations = combineInsights(plan.plan, qaInsights, plan.annotations);

  return {
    id: explain.humanId,
    seenAt: explain.seenAt,
    fingerprint: explain.fingerprint,
    label: formatTimestampShort(moment.unix(explain.seenAt)),
    runtime: explain.querySample.runtimeMs,
    ioMs: explain.totalBlkReadTime,
    ioBytes: explain.totalSharedBlksRead * blockSize,
    totCost: explain.totalCost,
    plan: {
      ...plan,
      annotations,
    },
  };
}

function useCurrentView(): ViewType {
  const location = useLocation();
  const [explainPrefs, setExplainPrefs] = useUserPreferences("explain");
  const viewParam = new URLSearchParams(location.search).get("view");
  const view = (viewParam ?? explainPrefs.lastView ?? "grid") as ViewType;
  useEffect(() => {
    if (["grid", "viz"].includes(view)) {
      setExplainPrefs({ lastView: view });
    }
  }, [view, setExplainPrefs]);
  return view;
}

function useViewLink(): (type: ViewType) => string {
  const location = useLocation();
  // preserve the URL state of time range selection
  const t = new URLSearchParams(location.search).get("t");

  return function viewLink(type: ViewType) {
    const newSearch: { view: ViewType; t?: string } = { view: type };
    if (t) {
      newSearch.t = t;
    }
    const result = new URLSearchParams(newSearch).toString();
    return "?" + result;
  };
}

const QueryPanel = ({ querySample }: { querySample: QuerySampleType }) => {
  if (!querySample) {
    return null;
  }
  const parameters = querySample.parameters;
  return (
    <Panel title="SQL Statement">
      <PanelSection>
        <ExpandableSQL sql={querySample.queryText} />
      </PanelSection>
      <PanelSection>
        <QueryParameters parameters={parameters} />
      </PanelSection>
    </Panel>
  );
};

export default ExplainPanel;
